import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X } from "lucide-react";
import { createPortal } from "react-dom";

export default function GrievanceStatusModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  currentStatus, 
  newStatus 
}) {
  const [note, setNote] = useState("");

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(note);
    setNote("");
  };

  const statusColors = {
    resolved: "text-emerald-500",
    rejected: "text-rose-500",
    in_progress: "text-blue-500",
  };

  const color = statusColors[newStatus] || "text-indigo-400";

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="relative w-full max-w-md bg-[#12141c] border border-white/10 rounded-[2rem] shadow-2xl overflow-hidden"
        >
          <div className="p-8">
            <div className="flex justify-between items-start mb-6">
              <div className="p-3 bg-indigo-500/10 rounded-2xl">
                <MessageSquare className="w-6 h-6 text-indigo-400" />
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/5 rounded-full transition-colors text-white/40 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <h3 className="text-xl font-bold text-white mb-2">Update Status</h3>
            <p className="text-sm text-white/40 mb-6 leading-relaxed">
              Moving grievance <span className="text-white font-medium">"{title}"</span> to{" "}
              <span className={`${color} font-bold uppercase`}>{newStatus.replace('_', ' ')}</span>.
              Add a note for the employee.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest ml-1">
                  Note (Reason for status change)
                </label>
                <textarea
                  autoFocus
                  required={newStatus === 'rejected' || newStatus === 'resolved'}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Provide details for the employee..."
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-sm text-white placeholder-white/20 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/50 transition-all resize-none h-32"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-4 rounded-2xl text-sm font-bold text-white/60 hover:bg-white/5 transition-all outline-none"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-2xl transition-all shadow-lg shadow-indigo-500/20 active:scale-95"
                >
                  Confirm
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
}
